/**
 * @module fx/AudioManager
 * Howler.js audio bridge — decouples game logic from audio playback.
 *
 * Architecture: the AudioManager accepts an injectable {@link SoundPlayer}
 * interface so unit tests can run without a real DOM / Web Audio API.
 * In production, pass the Howler-backed implementation from the app bootstrap.
 *
 * Combo pitch-up: Howler's `rate` option controls playback speed/pitch.
 * The slash sound plays at rate = 1 + combo × CONFIG.combo.pitchIncrement,
 * capped at CONFIG.combo.maxPitch.
 *
 * Sound categories:
 *  - slash  — "schwing" on valid slash (pitch-scaled by combo)
 *  - splat  — "splat" on fruit impact
 *  - bomb   — "explosion" on bomb hit
 *  - combo  — short "ding" on combo milestone
 *  - powerup — "sparkle" on power-up collect
 *  - ui     — menu button click
 *  - frenzy — wave announcement jingle
 */

import { CONFIG } from '../game-config.js';

/** Minimal sound playback interface — implemented by Howler in production. */
export interface SoundPlayer {
  /**
   * Plays the named sound clip.
   * @param name - Sound identifier (matches a loaded Howl sprite name or file key).
   * @param rate - Playback rate (1.0 = normal pitch).
   */
  play(name: string, rate?: number): void;
}

/** Named sound identifiers consumed by AudioManager. */
export type SoundName =
  | 'slash'
  | 'splat'
  | 'bomb'
  | 'combo'
  | 'powerup'
  | 'ui'
  | 'frenzy';

/**
 * Coordinates all in-game audio events.
 *
 * @param player - Injectable sound backend (Howler in prod, stub in tests).
 */
export class AudioManager {
  private readonly _player: SoundPlayer;
  /** Whether audio is globally muted (persisted in localStorage by UI). */
  private _muted = false;

  constructor(player: SoundPlayer) {
    this._player = player;
  }

  // ---------------------------------------------------------------------------
  // Event methods — call these from game logic
  // ---------------------------------------------------------------------------

  /**
   * Plays the slash sound, pitch-scaled by the current combo count.
   *
   * @param combo - Current combo count (0-based at time of this slash).
   */
  onSlash(combo: number): void {
    if (this._muted) return;
    const rate = Math.min(
      1 + combo * CONFIG.combo.pitchIncrement,
      CONFIG.combo.maxPitch,
    );
    this._player.play('slash', rate);
  }

  /** Plays the fruit splat sound on a successful hit. */
  onSplat(): void {
    if (this._muted) return;
    this._player.play('splat');
  }

  /** Plays the bomb explosion sound. */
  onBomb(): void {
    if (this._muted) return;
    this._player.play('bomb');
  }

  /**
   * Plays the combo milestone ding.
   * Only call at threshold combos (2, 3, 4, 5).
   */
  onComboMilestone(): void {
    if (this._muted) return;
    this._player.play('combo');
  }

  /** Plays the power-up collect sparkle. */
  onPowerUp(): void {
    if (this._muted) return;
    this._player.play('powerup');
  }

  /** Plays a UI button click sound (menu navigation). */
  onUiClick(): void {
    if (this._muted) return;
    this._player.play('ui');
  }

  /** Plays the frenzy wave announcement jingle. */
  onFrenzy(): void {
    if (this._muted) return;
    this._player.play('frenzy');
  }

  // ---------------------------------------------------------------------------
  // Mute control
  // ---------------------------------------------------------------------------

  /** Toggles global mute on/off. Returns the new muted state. */
  toggleMute(): boolean {
    this._muted = !this._muted;
    return this._muted;
  }

  /** Whether audio is currently muted. */
  get muted(): boolean {
    return this._muted;
  }

  /** Sets mute state directly (e.g. restoring from localStorage). */
  setMuted(value: boolean): void {
    this._muted = value;
  }
}
